// §IV · THE RED LINE (carrier) — ported from the Claude Design canon
// `R10 - §IV - canon F10.html` (project dd7b95d4, data-canon="R10-§IV-F10"; F10 (s949.d3, Н26) differs from F9 only by
// the one-step monotonic-descent line of v11 — bank THE SHAPE s504; F9 supersedes F8 per
// s714.d1 — only the number-plaque placement changed, F4 collision rule). Layout =
// R9 reference (styles/r10-iv.css; the §IV block is unchanged, checked by
// css-parity-iv.test.js). The deterministic copy generators — page title (geometry_titles
// s300), the italic phrase (italic banking 17/17), THE SHAPE (s305), THE MOVE (s316),
// intro (s304), eyebrow (s310.d1) — are transcribed VERBATIM from the canon; the chart,
// the evidence cards and the numbered points are LIVE from the recorded input.
//
// §IV is SELF-MANAGED like §II/§III: it renders its OWN artboard shell (the <section>
// with the §IV runner + "iv · pattern" footer), so it is mounted WITHOUT the generic
// Artboard wrapper. It is board-driven (a `view`).
//
// The ONE piece that is NOT a transcription is "where it stands now": it is the ENGINE's
// currentStanding (src/insights/render_keys.js) turned into the §IV register by
// sectionIVStanding.js — the same shared detector §III reads, different words. CD's canon
// used a hardcoded `split` shortcut for state 7; the engine expresses it structurally
// (a low/high cloud + 2 layers broken out the other way) and standingIV renders the same
// string. Boards feed engine-derived standing, so the copy is honest.
//
// §IV terminology says "carrier" (not §II's "the red line runs through"). The client
// re-fits (fitStageLabels full↔roman, fitSheet chart compression) are cosmetic and do
// not run under SSR; the board's declared labelMode renders — exactly §III's contract.

import { Fragment } from 'react';
import { standingIV } from '../sectionIVStanding.js';
import { placeTabs } from './ivTabPlacement.js';
import { findingParts, onePoint, shapeless } from '../../copy/findingBank.js';
import { italicPhrase, carrierEyebrow, NUMW } from '../../copy/chartCopy.js';
import { SHAPE, SHAPE_ONE, MOVE, INTRO } from '../../copy/passportCopy.js';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];

// EV_CAP — the evidence cards show at most the 6 most recent carrier stages (s378.d2). This is a
// DISPLAY cap on the cards only: the eyebrow fraction and the evidence subhead read the true M
// (the full carrier count, before the cap — s671.d1), the numbered points read the full list too
// (s676/s679.d1). The cap used to sit upstream in viewIV; it belongs here, at the one consumer
// that truncates. Number-plaque PLACEMENT at M>6 (where tabs begin to overlap) waits on the
// Field Mapping Spec §IV paragraph (s680.d1) — not touched here.
const EV_CAP = 6;

// The carrier-stage list filtered to valid stage indices — the ONE count the eyebrow fraction and
// the evidence subhead both read, so §IV and the passport always agree on M. Boundary = number of
// stages (the dimension carrierIdx lives in, s671.d1). The chart's own dereference guards (g.cols
// / cf bounds) are a SEPARATE render-safety concern (cc-30), applied where points/line actually
// index those arrays — not here, where we only count.
const carrierStageList = (v) =>
  (v.carrierIdx || []).filter((i) => Number.isInteger(i) && i >= 0 && i < (v.stages || []).length);

// layer palette — canon A.8 (same as §III)
const L = {
  Vitality: { name: 'vitality', color: '#D48A3C', dash: null },
  Security: { name: 'security', color: '#C7BFAE', dash: null },
  Growth: { name: 'growth', color: '#8FA168', dash: null },
  Authorship: { name: 'authorship', color: '#C7BFAE', dash: '6 4' },
  Wanting: { name: 'wanting', color: '#A86520', dash: null },
  Yourself: { name: 'being yourself', color: '#8FA168', dash: '6 4' },
  Lasting: { name: 'wanted to last', color: '#C7BFAE', dash: '1.5 4' },
};
const lname = (k) => L[k].name;

// ---- geometry_titles (s300) — БАНК ВЫНЕСЕН В ОБЩИЙ ДОМ (B6-2) ----
// Обе колонки (подлежащее по раскладу, предикат по геометрии), согласование по числу и правило
// F8 живут в `src/copy/findingBank.js`: те же строки печатает блок находки шага 4, и две копии
// одного судейства (`s300.d2`) разошлись бы молча. Здесь остаётся ТОЛЬКО одёжка — у §IV колонки
// одеты разными узлами, и это единственное, чем два потребителя различаются.
function pageTitle(v) {
  const { subject, predicate, separator } = findingParts(v);
  // ⚠️ `scatter` — ПОДЛЕЖАЩЕЕ БЕЗ ПРЕДИКАТА, и точка тогда уходит внутрь `<em>`, а не остаётся
  // снаружи: у этой редакции второго узла нет вовсе.
  if (predicate === null) return `<em>${subject}.</em>`;
  return `<em>${subject}</em>${separator}<span class="rd-em">${predicate}</span>`;
}

// ---- italic banking (17/17, plateau split by level_band) — ВЫНЕСЕН В ОБЩИЙ ДОМ (B8-1) ----
// `ITALIC`/`italicPhrase` живут в `src/copy/chartCopy.js`: тот же банк читает overlay шага 6.

// ---- answered-only view of the carrier (s495 · decision 3, variant A) ------------------------
//
// The per-stage mask lets `redLine.carrier` carry `null` on a stage where none of the carrier's
// layers were answered. Arithmetic on that array coerces null to 0, so `c[i] - c[i-1]` invents a
// delta: on [2, null, null, -2, -2] the page printed "the steepest drop, stage I to II" about a
// fall that never happened — the person simply had not touched the layer yet.
//
// Variant A, ratified: the copy generators read the COMPRESSED non-null subsequence, and the slot
// indices map back to real stage numbers. So the sentence counts only answered stages and names
// the stages the move actually happened between — which may be non-adjacent, and honestly so.
// No new copy: the same bank, correct numbers.
//
// The class itself was never wrong here — classifyGeometry already compresses before reading.
// It was the SLOTS inside the sentence that lied. Chart drawing is separate again (fillCarrier
// interpolates for the line); this function never touches the drawing.
function answered(c) {
  const vals = [], idx = [];
  c.forEach((x, i) => { if (x != null) { vals.push(x); idx.push(i); } });
  return { vals, idx };
}

// ---- THE SHAPE · bank of 15 forms (s305). ∅ = slot-free ----
function shapeSlots(c) {
  const n = c.length, d = [];
  for (let i = 1; i < n; i++) d.push(c[i] - c[i - 1]);
  const peak = c.indexOf(Math.max(...c)), low = c.indexOf(Math.min(...c));
  let steepDown = 0, steepUp = 0;
  d.forEach((x, i) => { if (x < d[steepDown]) steepDown = i; if (x > d[steepUp]) steepUp = i; });
  let turns = 0;
  for (let i = 1; i < d.length; i++) if (Math.sign(d[i]) && Math.sign(d[i - 1]) && Math.sign(d[i]) !== Math.sign(d[i - 1])) turns++;
  const peaks = [], lows = [];
  for (let i = 1; i < n - 1; i++) { if (c[i] > c[i - 1] && c[i] >= c[i + 1]) peaks.push(i); if (c[i] < c[i - 1] && c[i] <= c[i + 1]) lows.push(i); }
  return { n, peak, low, steepDown, steepUp, turns, peaks, lows, dropsAfterPeak: n - 1 - peak, risesAfterLow: n - 1 - low };
}
function theShape(v) {
  const a = answered(v.carrier), c = a.vals, s = shapeSlots(c);
  const R = (i) => ROMAN[a.idx[i]], W = (x) => NUMW[x] || x; // slot index → real stage number

  // Fewer than two answered points never reach here: the card itself is dropped (Н41, `onePoint`,
  // `s950.d3`). The old `[TODO-COPY]` guard that stood here for that state is gone with it.

  // count==1 branch (s504 bank addendum; rule s948.d1 / DoR s951). The count is the NUMBER IN THE
  // FORM'S SLOT — one step — not one answered point (one answered point is plateau, guarded above).
  // The plural rows below say "one steps down, one after another" when that number is 1; these four
  // forms get their own singular row. The other 11 have no such slot or cannot reach 1.
  const one = { 'monotonic-descent': s.n - 1, 'monotonic-ascent': s.n - 1, 'peak-then-fall': s.dropsAfterPeak, 'V-shape': s.risesAfterLow };
  const slots = {
    peak: R(s.peak), low: R(s.low),
    steepDownFrom: R(s.steepDown), steepDownTo: R(s.steepDown + 1), steepUpFrom: R(s.steepUp), steepUpTo: R(s.steepUp + 1),
    peak1: R(s.peaks[0]), peak2: R(s.peaks[1]), low1: R(s.lows[0]), low2: R(s.lows[1]),
    steps: W(s.n - 1), drops: W(s.dropsAfterPeak), rises: W(s.risesAfterLow), turns: W(s.turns),
  };
  if (SHAPE_ONE[v.geometryClass] && one[v.geometryClass] === 1) return SHAPE_ONE[v.geometryClass](slots);
  return (SHAPE[v.geometryClass] || SHAPE.irregular)(slots);
}

// ---- THE MOVE (s316: 1–3 labels) ----
function theMove(v) {
  const a = answered(v.carrier), c = a.vals;
  let k = 1, best = -1, signed = 0;
  for (let i = 1; i < c.length; i++) { const d = c[i] - c[i - 1]; if (Math.abs(d) > best) { best = Math.abs(d); k = i; signed = d; } }
  // F5: on a flat line the card is not printed at all. "the steepest drop" is a superlative laid over
  // geometry that cannot carry one — every delta is zero, so the loop falls back to the first segment
  // and names a drop that does not happen, while THE SHAPE beside it says the line barely moves.
  // The two-card grid already exists in canon (rd-claims-2), so dropping it needs no new copy.
  if (c.length < 2 || v.geometryClass === 'plateau' || best === 0) return null;
  const word = signed > 0 ? MOVE.word.up : MOVE.word.down;
  const from = ROMAN[a.idx[k - 1]], to = ROMAN[a.idx[k]], st = v.stages[a.idx[k]] || {};
  const head = MOVE.head(word, from, to);
  const flags = []; // the flat-line flag is gone: that state no longer reaches here, it returns null above
  const labels = (st.bg1 && st.bg1.length) ? st.bg1 : null;
  if (labels) return { t: MOVE.bg1(head, NUMW[labels.length], labels), src: 'BG1', flags };
  if (st.bg2) { flags.push('источник меток: BG2 (fuel) — своей формулировки в спеке нет'); return { t: MOVE.bg2(head, NUMW[1], st.bg2), src: 'BG2', flags }; }
  flags.push('источник меток: нет');
  return { t: MOVE.none(head), src: 'none', flags };
}

// ---- intro · six branches by carrier config (s304) ----
function intro(v) {
  const m = v.members.map(lname);
  return (INTRO[v.carrierConfig] || INTRO.default)(m);
}

// ---- chart_eyebrow_right · bank of six forms (s310.d1) + overflow rule ----
// Банк брови и `EYB_CAP` живут в общем узле `src/copy/chartCopy.js` (`carrierEyebrow`, B7-3): ту же
// бровь печатает шаг 5, и копия здесь расходилась бы молча. У §IV своё печатное имя слоя.
const eyebrowRight = (v) => carrierEyebrow(v, lname);

// ================= geometry (R9 §IV numbers) =================
const G = { W: 1000, H: 320, AXIS_LEFT: 60, AXIS_RIGHT: 988, Y_TOP: 28, Y_BOT: 264 };
G.Y_MID = (G.Y_TOP + G.Y_BOT) / 2;
const yVal = (v) => G.Y_MID - (v / 3) * ((G.Y_BOT - G.Y_TOP) / 2);
// Якорь «сейчас» для оси §IV, в десятичных годах с МЕСЯЧНЫМ разрешением — ось тут месячная,
// поэтому это не `READING_YEAR` (2026) и не может им стать: значение запечено каноном, и любой
// другой год сдвинет x-координаты всех точек. Смысл тот же — конец дуги, когда своего конца нет.
const NOW = 2026.58;
function pdate(s) { if (!s) return null; const [m, y] = s.split('/').map(Number); return y + (m - 0.5) / 12; }
function smooth(pts, t = 0.5) {
  if (pts.length < 2) return pts.length ? `M ${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}` : '';
  let d = `M ${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    d += ` C ${(p1.x + (p2.x - p0.x) * (t / 3)).toFixed(2)},${(p1.y + (p2.y - p0.y) * (t / 3)).toFixed(2)} ${(p2.x - (p3.x - p1.x) * (t / 3)).toFixed(2)},${(p2.y - (p3.y - p1.y) * (t / 3)).toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }
  return d;
}
function geom(v) {
  const st = v.stages, N = st.length;
  const first0 = st[0].openStart ? pdate(st[0].end) : pdate(st[0].start);
  // ПОРЯДОК: реальный конец побеждает якорь. До cc-31 условием стоял `current`, и проставленный
  // конец текущего этапа отбрасывался — та же ошибка, что была в §I/§II/§VII. Здесь она стоила
  // не только неверного года: ось строится по `last`, и на сабмите 2028 точка текущего этапа
  // уезжала на x ≈ 1112 при ширине борда 1000, то есть маркер и последний сегмент красной
  // линии просто пропадали за краем листа. Без NaN и без падения — молча.
  // Канон-борды не двигаются: у всех у них `end = null` на текущем этапе (замер cc-31).
  const lastEnd = pdate(st[N - 1].end);
  // Якорь закрывает ось, только если он не РАНЬШЕ самой поздней известной даты. Иначе точки
  // этапов, лежащих позже него, считаются за правым краем: замер матрицей согласия дал x до
  // 6556 при ширине борда 1000 — маркеры и хвост красной линии уходили с листа (QA cc-31,
  // второй заход: первая починка выбрала правильный ЯКОРЬ, но не проверила, что он накрывает
  // ДАННЫЕ). Канон не двигается: там все даты раньше 2026.58.
  // У ЭТАПА С ОТКРЫТЫМ НАЧАЛОМ СВОЕГО НАЧАЛА НЕТ — и его `start` в домен не входит. Это не
  // придирка: борд v08 несёт `openStart: true` И проставленный `start` 09/2004, а домен CD
  // начинает с его КОНЦА (06/2009). Первая версия левого гарда затянула 2004.7 в минимум и
  // сдвинула ось всего борда — байт-парити упала, и правильно (замер 21.08). Открытое начало
  // означает «до этой даты записи нет», так что дата слева от неё — не данные.
  const known = st.flatMap((x) => [x.openStart ? null : pdate(x.start), pdate(x.end)]).filter(Number.isFinite);
  const last = Math.max(Number.isFinite(lastEnd) ? lastEnd : NOW, ...(known.length ? known : [NOW]));
  // ЛЕВАЯ ПОЛОВИНА ТОГО ЖЕ ГАРДА (QA cc-33). Строкой выше `last` накрывает САМУЮ ПОЗДНЮЮ дату,
  // потому что этап позже якоря уезжал за ПРАВЫЙ край. Зеркальная сторона написана не была: начало
  // оси бралось у ПЕРВОГО этапа, а не у самой ранней даты, и этап, лежащий раньше него, считался
  // за ЛЕВЫМ краем ровно так же. Замер 21.08, вход `a 2005→2010, b 2000→2003` (validateWizardInput
  // чист): маркер этапа II и его подпись печатались на **x = −589.6** при ширине борда 1000 —
  // с листа уходили обе точки и хвост красной линии, без NaN и без падения.
  // Хронология — совещательное предупреждение, а не ошибка (`wizardInputWarnings`), значит импорт
  // и API такой ввод донесут. Ось обязана накрыть ДАННЫЕ, а не первый элемент массива.
  // Канон не двигается: у всех бордов и датасетов `first0` И ЕСТЬ минимум известных дат.
  const first = Number.isFinite(first0) && known.length ? Math.min(first0, ...known) : first0;
  // ── ТРИ ГАРДА, все три поймал QA cc-31, все три печатались молча ───────────────────────
  //
  // 1. Даты могут отсутствовать ЦЕЛИКОМ (импорт, краевой ввод). Тогда `first` был `null`,
  //    `Math.floor(null)` давал 0, и цикл тиков шёл от нулевого года: ось печатала **508
  //    подписей** поверх листа. §IV была единственной секцией без бездатной деградации —
  //    теперь она индексная, как §III: колонки ровно, года нет ни одного.
  // 2. `xE` стоял на `s.current`, а не на «текущий И последний». При флаге на среднем этапе
  //    колонка растягивалась до правого края, и колонки шли не по порядку — красная линия
  //    разворачивалась назад. Ровно тот же предикат, что чинили в §I (`nowEndsArc`).
  // 3. Пустой `end` у НЕтекущего этапа давал `xYear(null)` → точка на x ≈ −34 700 при ширине
  //    борда 1000. Теперь конец ищется по цепочке: свой конец → начало следующего этапа →
  //    индексная позиция. Выдумывать год не надо, надо не рисовать за краем.
  const dated = Number.isFinite(first) && Number.isFinite(last);
  const span = dated ? Math.max(last - first, 1) : 1;
  const xIdx = (i) => G.AXIS_LEFT + (N <= 1 ? 0 : (i / N) * (G.AXIS_RIGHT - G.AXIS_LEFT));
  const xYear = (y) => (Number.isFinite(y) && dated
    ? G.AXIS_LEFT + ((y - first) / span) * (G.AXIS_RIGHT - G.AXIS_LEFT)
    : null);
  const cols = st.map((s, i) => {
    const isLast = i === N - 1;
    const nextStart = i + 1 < N ? pdate(st[i + 1].start) : null;
    const xS = s.openStart ? 0 : (xYear(pdate(s.start)) ?? xIdx(i));
    const xE = (s.current && isLast) ? G.AXIS_RIGHT
      : (xYear(pdate(s.end)) ?? xYear(nextStart) ?? xIdx(i + 1));
    return { ...s, roman: ROMAN[i], xStart: xS, xEnd: xE, xMid: (xS + xE) / 2 };
  });
  const ticks = [];
  if (dated) {
    const yS = Math.floor(first), yE = Math.floor(last);
    for (let y = yS; y < yE; y += 4) ticks.push(y);
    if (ticks.length && ticks[ticks.length - 1] < yE - 2) ticks.push(yE - 2);
  }
  return { cols, first, last, span, xYear, ticks, dated };
}

// ---- carrier null-fill (s495 · decision 3, Kirill 2026-08-17) ----
//
// The per-stage mask (s443.d5 Ф2) makes `redLine.carrier` emit `null` on a stage where none of
// the carrier's layers were answered. Measured: with a real person touching different sliders on
// different stages that happens on 88–100% of maps, so it is the normal case, not an edge one.
// Before this, `yVal(null)` collapsed to 0 and the null was PLOTTED — the line drew an answer the
// person never gave, exactly the lie the mask removed at engine level.
//
// The rule is the board canon `filled()` (`s5 Red Line v6`), extended here from the s4 silhouette
// (E2, s493) to the §IV carrier. The line is always solid at full weight — a gap or a dash would
// say "not enough data here" with the weight of the composition, which the lock forbids:
//   · interior null → linear interpolation between the neighbours
//   · trailing null → the last known value, flat to the right edge (NOT a shortened line: the
//     "where it stands now" block sits at the right, truncation would strand it in empty space)
//   · leading null  → the first known value, flat from the left edge
//
// STRICTLY a render concern — the filled array never leaves this function. `redLine.carrier` keeps
// its nulls on purpose: `geometry.js` compresses to non-null before class and reading (:20, :241),
// and `currentStanding` reads the stage values directly (`render_keys.js:97`), never the curve.
// Feeding a filled array back would turn shape into a claim. Interpolation carries the shape and
// makes no statement — s4-handoff п.4 / s5-handoff п.5.
//
// DIVERGENCE from the board engine, deliberate: `filled()` returns all-zeros for a fully untouched
// series. Here that returns null and the caller draws no line — a flat line along zero is "an
// answer nobody gave" in its purest form. Unreachable behind the s3 floor (≥2 touched per stage);
// removing the section outright would need the §V-style engine flag, which §IV has not got — spec
// item, not invented here.
export function fillCarrier(c) {
  if (!Array.isArray(c) || !c.some((x) => x != null)) return null;
  const out = c.slice();
  let prev = null;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) { prev = i; continue; }
    let nx = null;
    for (let j = i + 1; j < out.length; j++) if (c[j] != null) { nx = j; break; }
    if (prev === null) out[i] = c[nx];
    else if (nx === null) out[i] = c[prev];
    else out[i] = c[prev] + (c[nx] - c[prev]) * ((i - prev) / (nx - prev));
  }
  return out;
}

// ---- the SVG chart, built as React elements (verbatim CD `chart`) ----
function Chart({ v, g }) {
  const N = v.stages.length;
  const cf = fillCarrier(v.carrier); // null = nothing answered anywhere → draw no line
  // cc-30 guard, and this is the ROOT of the §IV crash — not the markers, the line itself.
  // The carrier and the layer series are indexed by SLIDER ROW; `g.cols` is indexed by STAGE.
  // The contract says the two arrays are the same length (`validateWizardInput` reports
  // "wizard.stages length N != sliders M"), but no render path calls the validator, so a
  // mismatched passport reached `g.cols[i].xMid` with i past the last column and threw
  // `Cannot read properties of undefined (reading 'xMid')` — taking the WHOLE assembled document
  // down, since §IV is never rendered alone. We draw the columns we actually have and stop:
  // inventing a column for a stage that does not exist would be worse than a short line.
  // No-op on canon and on any valid input — there cf.length === g.cols.length.
  const NCOL = g.cols.length;
  const onCols = (arr) => (arr.length > NCOL ? arr.slice(0, NCOL) : arr);
  const pts = cf ? onCols(cf).map((val, i) => ({ x: g.cols[i].xMid, y: yVal(val) })) : [];
  // Н41 (`s950.d1`): one answered point draws one point and no line — `fillCarrier` would lay a flat
  // line through the whole width at that height, the "line from one point" the rule forbids.
  const lone = onePoint(v);
  const red = cf && !lone ? smooth(pts) : '';
  // pale lines: a gap is filled exactly like the red line's (fillCarrier); a layer never answered
  // at all draws nothing (Н42 · s950.d1)
  const pale = (v.carrierSourceLines || []).filter((k) => v.layerValues && fillCarrier(v.layerValues[k])).map((k, pi) => {
    const vals = onCols(fillCarrier(v.layerValues[k]));
    const p = vals.map((val, i) => ({ x: g.cols[i].xMid, y: yVal(val) }));
    // coincidence is judged against the line actually drawn, so a filled point compares like any other
    const same = !!cf && vals.every((val, i) => Math.abs(val - cf[i]) < 1e-6);
    return (
      <path key={pi} data-layer={k} {...(same ? { 'data-coincident': 'true' } : {})} d={smooth(p)} fill="none" stroke={L[k].color} strokeWidth="1.2" {...(L[k].dash ? { strokeDasharray: L[k].dash } : {})} opacity=".38" strokeLinecap="round" />
    );
  });
  // cc-30 guard. `carrierIdx` is indexed against the SLIDER array; `g.cols` and `cf` are indexed
  // by STAGE, and nothing enforces equal length at render time (validateWizardInput would flag
  // it, but no render path calls the validator). An index past the last column threw
  // `Cannot read properties of undefined (reading 'xMid')` and took the WHOLE assembled document
  // down with it — §IV is not rendered alone. Out-of-range now means "draw no marker for that
  // stage", which is the honest degrade: we cannot point at a column that does not exist.
  // No-op on canon — every board's indices are in range, so byte-parity is untouched.
  const inRange = (i) => Number.isInteger(i) && i >= 0 && i < g.cols.length && (!cf || i < cf.length);
  const carrierIdx = (v.carrierIdx || []).filter(inRange);
  const carrierM = carrierStageList(v).length; // true M for the eyebrow fraction (data boundary)
  const carrierSet = new Set(carrierIdx);
  // Н41: on one point every non-carrier stage is unanswered — no marks at a height nobody gave.
  const muted = !cf || lone ? [] : g.cols.map((c, i) => carrierSet.has(i) ? null : (
    <g key={i}>
      <circle cx={c.xMid.toFixed(1)} cy={yVal(cf[i]).toFixed(1)} r="5.4" fill="#0A0B0D" />
      <circle cx={c.xMid.toFixed(1)} cy={yVal(cf[i]).toFixed(1)} r="3.5" fill="#5A554A" opacity=".65" />
    </g>
  ));
  const dots = (!cf ? [] : carrierIdx).map((idx, j) => {
    const x = g.cols[idx].xMid, y = yVal(cf[idx]);
    return (
      <g key={`d${j}`}>
        <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r="14" fill="rgba(200,72,43,.18)" />
        <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r="8" fill="#C8482B" stroke="#0A0B0D" strokeWidth="2" />
      </g>
    );
  });
  // §IV number plaques — F4 collision placement (s714.d1). The rule lives in ivTabPlacement.js:
  // SSR and the jsdom golden capture have no getTotalLength, so a pure-JS arc-length sampler
  // replaces the browser path measurement (a verbatim port would throw at SSR = the cc-30 crash
  // class). `red` is the same smoothed path drawn below, and `cf` gives the same point positions
  // as the muted dots, so the sampled curve and obstacles match the golden by construction. Was a
  // measurement-free rule (dx=±28, dy=flip?38:-36) that seated plaque 01 on the red line at 0px
  // clearance on ~12 boards; F4 pushes off obstacles by side then tier before collision, not after.
  const picks = (!cf || (!red && !lone)) ? [] : placeTabs(carrierIdx, g.cols, cf, red, yVal, G);
  const tabs = picks.map((pk, j) => {
    const rect4 = [pk.r.x0, pk.r.y0, pk.r.x1, pk.r.y1].map((n) => n.toFixed(1)).join(',');
    const lead4 = [pk.s.x1, pk.s.y1, pk.s.x2, pk.s.y2].map((n) => n.toFixed(1)).join(',');
    return (
      <g key={`t${j}`} data-tab-n={pk.n} data-tab-tier={pk.tier} data-tab-side={pk.side < 0 ? 'left' : 'right'}
        {...(pk.soft ? { 'data-tab-leader-crosses': pk.soft } : {})}
        {...(pk.fallback ? { 'data-tab-fallback': pk.fallback } : {})}
        data-tab-rect={rect4} data-tab-leader={lead4}
        transform={`translate(${pk.cx.toFixed(1)}, ${(pk.y + pk.dy).toFixed(1)})`}>
        <line x1="0" y1={pk.flip ? -9 : 9} x2={-pk.dx} y2={pk.flip ? -(Math.abs(pk.dy) - 4) : (Math.abs(pk.dy) - 4)} stroke="rgba(200,72,43,.7)" strokeWidth=".7" strokeDasharray="1.5 2.5" />
        <rect x={pk.rx} y="-9" width="26" height="16" fill="#C8482B" />
        <text x={pk.rx + 13} y="2" textAnchor="middle" style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', letterSpacing: '.14em', fill: '#E8E2D4', fontWeight: 500 }}>{pk.n}</text>
      </g>
    );
  });
  const lastTick = g.ticks[g.ticks.length - 1];
  const eyb = eyebrowRight(v);
  const N1 = v.stages[N - 1];
  return (
    <div className="rd-chart-wrap">
      <span className="r4-corner tl" /><span className="r4-corner tr" /><span className="r4-corner bl" /><span className="r4-corner br" />
      <div className="r6-rd-gtag" data-field="chart_eyebrow_left">
        <b>pattern</b><span className="sep">·</span><span>long arc</span>
        {/* fraction = true M (carrier count, before the cards' display cap) / stage count. Was
            `v.carrierIdx.length` raw — the ONE consumer without a filter (doизмер №4), and it
            read the capped six instead of the true count (s671.d1). Both fixed by carrierStageList. */}
        {carrierM && N ? (<><span className="sep">·</span><span className="num">{carrierM}<em>/</em>{N}</span></>) : null}
      </div>
      <div className="r6-rd-eyb" data-field="chart_eyebrow_right" {...(eyb.capped ? { 'data-cap-applied': 'eyebrow_right → безымянная форма расклада' } : {})}>
        <span>{eyb.label}</span><span className="sep">·</span><b>{eyb.body}</b>
      </div>
      <svg className="rd-chart" data-field="chart" viewBox={`0 0 ${G.W} ${G.H + 36}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`rl-${v.id}`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={G.W} y2="0">
            <stop offset="0" stopColor="#7A1E10" stopOpacity=".55" /><stop offset=".35" stopColor="#C8482B" stopOpacity="1" /><stop offset=".75" stopColor="#E8623F" stopOpacity="1" /><stop offset="1" stopColor="#C8482B" stopOpacity=".75" />
          </linearGradient>
          <filter id={`rg-${v.id}`} x="-10%" y="-30%" width="120%" height="160%"><feGaussianBlur in="SourceGraphic" stdDeviation="3" /></filter>
        </defs>
        <line x1="0" y1={G.Y_BOT + 14} x2={G.W} y2={G.Y_BOT + 14} stroke="#3A3E48" strokeWidth=".5" strokeDasharray="2 4" />
        <line x1={G.AXIS_LEFT} y1={G.Y_TOP - 8} x2={G.AXIS_LEFT} y2={G.Y_BOT + 8} stroke="#3A3E48" strokeWidth=".75" strokeDasharray="2 4" />
        <line x1="0" y1={yVal(0)} x2={G.W} y2={yVal(0)} stroke="rgba(143,136,122,.18)" strokeDasharray="2 6" strokeWidth=".6" />
        <g data-field="chart_pale_lines">{pale}</g>
        <path d={red} fill="none" stroke="#C8482B" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" opacity=".22" filter={`url(#rg-${v.id})`} />
        <path d={red} fill="none" stroke={`url(#rl-${v.id})`} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
        <g data-field="chart_muted_dots">{muted}</g>
        <g data-field="chart_numbered_points">{dots}{tabs}</g>
        <g data-field="chart_year_axis" style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', letterSpacing: '.16em' }}>
          {g.ticks.map((y) => (
            <text key={y} x={g.xYear(y).toFixed(1)} y={G.Y_BOT + 32} textAnchor="middle" fill={y === lastTick ? '#D48A3C' : '#5A554A'}>{`'${String(y).slice(-2)}`}</text>
          ))}
          {N1.current ? (
            <text x={(G.AXIS_RIGHT - 4).toFixed(1)} y={G.Y_BOT + 32} textAnchor="end" fill="#8F887A">now</text>
          ) : (
            <text x={(G.AXIS_RIGHT - 4).toFixed(1)} y={G.Y_BOT + 32} textAnchor="end" fill="#8F887A">{`'${String(Math.floor(g.last)).slice(-2)}`}</text>
          )}
        </g>
        <g data-field="chart_stage_labels">
          {g.cols.map((c, i) => (
            <text key={i} x={c.xMid.toFixed(1)} y={G.Y_TOP - 22} textAnchor="middle" className={`rd-chart-stagename${carrierSet.has(i) ? ' rd-chart-stagename-active' : ''}`} data-roman={c.roman} data-full={`${c.roman} · ${c.name}`} data-col-w={(c.xEnd - c.xStart).toFixed(1)} data-label-mode={v.labelMode}>
              {v.labelMode === 'roman' ? c.roman : `${c.roman} · ${c.name}`}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}

// ---- "where it stands now" — engine standing rendered in the §IV register ----
function StandingSegs({ segments }) {
  return (
    <>
      {segments.map((s, i) => s.amber ? <span className="lp-note-amber" key={i}>{s.text}</span> : <Fragment key={i}>{s.text}</Fragment>)}
    </>
  );
}

// ---- evidence cards (verbatim CD `evidenceCards`) ----
// Cap on the evidence quote AS RENDERED on the §IV passport card (s840.d1; norm set by Kirill,
// cc-99). §IV lives in ONE surface — the fixed A4 artboard (r10.css `--page-w:1240px`); it has no
// screen/responsive context (no @media, no tap-to-expand in code — that's the un-started wizard
// track). Its NARROW slot is the two-column layout (.rd-fit-cols, M 5–6 cards): 433 px content @
// Newsreader 13.5/1.45. 260 chars ≈ FOUR lines there (browser-measured cc-99 against the real
// r10-iv.css cascade). The real §IV quote corpus maxes at 233 chars (median 88), so no live quote
// is cut — the ellipsis is a guard against an outlier, not a screen element. This is NOT the step-5
// board: that slot was 284 px / 12.5 px (a different surface), and its "233 = five lines" probe does
// NOT transfer here — measuring against it was the s840.d1 miss. At a different slot RECOMPUTE for
// the target line count; do NOT carry 260 over. §VI caps (STACK_MAX/QUOTE_N_MAX) are a different
// surface — deliberately not reused here.
// DISPLAY-ONLY: the stored quote (viewIV `:276`, the stage cut sheet's full text) is untouched.
export const EV_QUOTE_CAP = 260;
export function capQuote(s) {
  if (typeof s !== 'string' || s.length <= EV_QUOTE_CAP) return s; // ≤ cap → as-is, no ellipsis
  let t = s.slice(0, EV_QUOTE_CAP);
  const sp = t.lastIndexOf(' ');
  if (sp > 0) t = t.slice(0, sp); // back off to the last whole word
  return t.replace(/[\s,;:.—-]+$/, '') + '…'; // strip trailing punct, one-char ellipsis (probe form)
}

function Evidence({ v }) {
  // Same cc-30 guard as the chart, second throw site: an out-of-range index made `v.stages[idx]`
  // undefined and `s.name` threw. The list is filtered to valid stage indices (data boundary).
  // No-op on canon — every board's indices are in range.
  const list = carrierStageList(v);
  if (!list.length) return null;
  // subhead reads the TRUE M (before the display cap, s671.d1); the cards show the 6 most recent
  // (EV_CAP). On canon (M ≤ 6) `shown === list` and byte-parity is untouched. The card/point number
  // alignment at M>6 waits on the plaque-placement paragraph (s680.d1) — see EV_CAP note above.
  const M = list.length;
  const shown = list.length > EV_CAP ? list.slice(-EV_CAP) : list;
  return (
    <>
      <div className="rd-ev-head" data-field="evidence_subhead">{NUMW[M]} {M === 1 ? 'stage' : 'stages'} it ran through</div>
      <div className="rd-evidence">
        {shown.map((idx, j) => {
          const s = v.stages[idx];
          if (!s.name) return null;
          const q = capQuote((v.evidence.find((e) => e.stage === idx) || {}).quote); // s840.d1 (render-only)
          const dates = s.datesMissing ? null : (s.openStart ? `(open) — ${s.end}` : (s.current ? `${s.start} — present` : `${s.start} — ${s.end}`));
          return (
            <article className="r4-ev" data-n={String(j + 1).padStart(2, '0')} data-field={`evidence_cards[${j}]`} key={j}>
              <div className="r4-ev-name" data-field="evidence_cards[j].stage_name"><em>{s.name}</em></div>
              {dates ? <div className="r4-ev-date" data-field="evidence_cards[j].date_range">{dates}</div> : null}
              <div className="r4-ev-pills">
                {s.bg1 && s.bg1.length ? <span className="r4-ev-pill trig" data-field="evidence_cards[j].ev_pill_trig">↓ {s.bg1[0]}</span> : null}
                {s.bg2 ? <span className="r4-ev-pill thru" data-field="evidence_cards[j].ev_pill_thru">→ {s.bg2}</span> : null}
              </div>
              {q
                ? <p className="r4-ev-quote" data-field="evidence_cards[j].verbatim_quote">{q}</p>
                : <p className="r4-ev-quote silent" data-field="evidence_cards[j].verbatim_quote">the line's shape carries this one.</p>}
            </article>
          );
        })}
      </div>
    </>
  );
}

export default function SectionIV({ view }) {
  const v = view;
  const g = geom(v);
  const standing = standingIV(v.standing); // null → the "where it stands now" card is dropped
  const mv = theMove(v); // null on a flat line → the "the move" card is dropped

  const cards = [
    shapeless(v) || onePoint(v) ? null : (
      <div className="rd-claim" key="shape">
        <div className="rd-claim-mark" data-field="claim_cards.lens_label">the shape</div>
        <p className="rd-claim-p" data-field="claim_cards.the_shape.description">{theShape(v)}</p>
      </div>
    ),
    standing ? (
      <div className="rd-claim" key="stands">
        <div className="rd-claim-mark" data-field="claim_cards.lens_label">where it stands now</div>
        <p className="rd-claim-p" data-field="claim_cards.where_it_stands_now.description"><StandingSegs segments={standing.segments} /></p>
      </div>
    ) : null,
    mv ? (
      <div className="rd-claim" key="move">
        <div className="rd-claim-mark" data-field="claim_cards.lens_label">the move</div>
        <p className="rd-claim-p" data-field="claim_cards.the_move.description" {...(mv.flags && mv.flags.length ? { 'data-flag': mv.flags.join(' · ') } : {})}>{mv.t}</p>
      </div>
    ) : null,
  ].filter(Boolean);

  return (
    <section
      className="artboard red-page r4 r4-redline"
      data-screen-label={v.screen}
      data-run={v.source}
      data-canon="R10-§IV-F10"
      data-stage-count={v.stages.length}
      data-carrier-config={v.carrierConfig}
      data-carrier-count={v.members.length}
    >
      <div className="ab-paper" />
      <div className="ab-grain" />
      <header className="ab-runner">
        <span data-field="section_header_left"><span className="runner-roman">IV</span>the red line</span>
        <span className="runner-meta" data-field="section_header_right">life map · the reading</span>
      </header>
      <div className="rd-sheet">
        <div className="rd-hd">
          <div className="rd-roman">IV</div>
          <h2 className="rd-title" data-field="page_title" dangerouslySetInnerHTML={{ __html: pageTitle(v) }} />
        </div>
        <p className="rd-italic" data-field="intro_paragraph" dangerouslySetInnerHTML={{ __html: intro(v) }} />
        <Chart v={v} g={g} />
        {italicPhrase(v) === null ? null : <p className="rd-iq" data-field="chart_italic_phrase">{italicPhrase(v)}</p>}
        {/* F5: the grid narrows by CARD COUNT, not by whether "where it stands now" is present.
            A plateau that keeps its stands block would otherwise put two cards in a three-column
            grid and leave a hole. Both droppable cards are counted the same way. */}
        <div className={`rd-claims${cards.length < 3 ? ' rd-claims-2' : ''}`}>{cards}</div>
        <Evidence v={v} />
        <div className="rd-cross">
          <span data-field="cross_chapter_pointer"><span className="rd-cross-arrow">→</span> the patterns show this reading alongside other curves that carry the same finding.</span>
          <span className="rd-cross-tag" data-field="cross_ref_tag">cross-ref</span>
        </div>
      </div>
      <footer className="ab-foot">
        <span data-field="footer_left"><span className="foot-em">life map</span> <span className="foot-faint">·</span> the red line <span className="foot-faint">·</span> <span style={{ color: 'var(--ember)' }}>the carriers</span></span>
        <span className="foot-page" data-field="footer_right">iv · pattern</span>
      </footer>
    </section>
  );
}
